import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch.js";
import Swal from "sweetalert2";
import { saveFile } from "../../../common/utils/saveFile.util.js";
import { createInfractionService } from "../services/createInfraction.service.js";
import { updateInfractionService } from "../services/updateInfraction.service.js";
import { deleteInfractionService } from "../services/deleteInfraction.service.js";
import { exportInfractionsToExcelService } from "../services/exportInfractionsToExcel.service.js";

const initialState = {
  id: null,
  code: "",
  date: "",
  driverName: "",
  driverIdentification: "",
  plate: "",
  type: "",
  department: "",
  city: "",
  authority: "",
  status: "",
};

export const useInfractionCommand = (refresh) => {
  const fetchData = useFetch();
  const [infraction, setInfraction] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = ({ name, value }) => {
    setInfraction((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const setDefaultValues = (defaultValues) => {
    setInfraction(defaultValues || initialState);
  };

  const createInfraction = async () => {
    try {
      const isValid = validations(infraction);
      if (!isValid) return;
      await createInfractionService(fetchData)(infraction);
      setInfraction(initialState);
      setErrors({});
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Infracción creada correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al crear la infracción",
        showConfirmButton: false,
        timer: 1500,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async (filters) => {
    try {
      const response = await exportInfractionsToExcelService(fetchData)(
        filters
      );
      if (response) {
        saveFile(response, "infracciones.xlsx");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const updateInfraction = async () => {
    try {
      const isValid = validations(infraction);
      if (!isValid) return;
      setLoading(true);
      await updateInfractionService(fetchData)(infraction);
      setInfraction(initialState);
      setErrors({});
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Infracción actualizada correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al actualizar la infracción",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteInfraction = async (id) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar esta infracción?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;

      setLoading(true);
      await deleteInfractionService(fetchData)(id);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "Infracción eliminada correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al eliminar la infracción",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const validations = (infraction) => {
    const newErrors = {};

    if (!infraction.code) {
      newErrors.code = "El campo código es requerido";
    }

    if (!infraction.date) {
      newErrors.date = "El campo fecha es requerido";
    }

    if (!infraction.driverName) {
      newErrors.driverName = "El campo nombre del conductor es requerido";
    }

    if (!infraction.driverIdentification) {
      newErrors.driverIdentification =
        "El campo identificación del conductor es requerido";
    }

    if (!infraction.plate) {
      newErrors.plate = "El campo placa es requerido";
    }

    if (!infraction.type) {
      newErrors.type = "El campo tipo es requerido";
    }

    if (!infraction.department) {
      newErrors.department = "El campo departamento es requerido";
    }

    if (!infraction.city) {
      newErrors.city = "El campo ciudad es requerido";
    }

    if (!infraction.authority) {
      newErrors.authority = "El campo autoridad es requerido";
    }

    if (!infraction.status) {
      newErrors.status = "El campo estado es requerido";
    }

    const hasErrors = Object.keys(newErrors).length > 0;
    setErrors(newErrors);
    return !hasErrors;
  };

  return {
    infraction,
    loading,
    errors,
    handleChange,
    setDefaultValues,
    createInfraction,
    updateInfraction,
    deleteInfraction,
    exportToExcel,
  };
};
